import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Modern (jjfv2) header / hero / footer
import HeaderV2 from '../../components/headerV2/HeaderV2';
import FooterV2 from '../../components/footerV2/FooterV2';
import AboutHero from '../../components/aboutHero/AboutHero';
import BlogSidebar from './BlogSidebar';

import { fetchBlogBySlug, formatBlogDate } from '../../api/cmsApi';

// CSS imports
import '../AboutUsPage/aboutV2.css';
import './blogs.css';

/**
 * Single blog page. The layout is identical to the old hardcoded blog
 * components — only the content now comes from the CMS.
 */
export default function BlogPost() {
    const { slug } = useParams();

    const [post, setPost] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | missing | error

    useEffect(() => {
        const controller = new AbortController();

        setStatus('loading');
        setPost(null);
        window.scrollTo(0, 0);

        fetchBlogBySlug(slug, { signal: controller.signal })
            .then((data) => {
                if (controller.signal.aborted) return;
                if (!data) {
                    setStatus('missing');
                    return;
                }
                setPost(data);
                setStatus('ready');
            })
            .catch((error) => {
                if (controller.signal.aborted || error.name === 'AbortError') return;
                console.error('Could not load blog:', error);
                setStatus('error');
            });

        return () => controller.abort();
    }, [slug]);

    const title = post ? `${post.title} | JJF India` : 'Blogs | JJF India';
    const description = post?.excerpt || '';
    const canonical = `https://jjfindia.com/our-bulletin/${slug}`;

    return (
        <>
            <Helmet>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap"
                    rel="stylesheet"
                />

                {/* Basic Meta Tags */}
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta name="robots" content={status === 'missing' ? 'noindex, follow' : 'index, follow'} />

                {/* Canonical URL */}
                <link rel="canonical" href={canonical} />

                {/* Open Graph */}
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:url" content={canonical} />
                {post?.thumbnail && <meta property="og:image" content={post.thumbnail} />}

                {/* Twitter Cards */}
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
            </Helmet>

            <div className="jjfv2">
                <HeaderV2 />
                <AboutHero
                    eyebrow="Our Bulletin"
                    title="Blogs"
                    crumbs={[{ label: 'Home', to: '/' }, { label: 'Blogs' }]}
                />
            </div>
            <main className="page_content">
                <div style={{ paddingBottom: "60px", paddingTop: "60px" }} className="container">
                    <div className='single-blog-container' >
                        <div className="blog-article">
                            {status === 'loading' && <p>Loading…</p>}

                            {status === 'missing' && (
                                <>
                                    <h1>Blog not found</h1>
                                    <p>This blog may have been moved or removed. <a href="/our-bulletin/blogs">Browse all blogs</a>.</p>
                                </>
                            )}

                            {status === 'error' && (
                                <>
                                    <h1>Something went wrong</h1>
                                    <p>We could not load this blog right now. Please try again in a moment.</p>
                                </>
                            )}

                            {status === 'ready' && post && (
                                <>
                                    <h1>{post.title}</h1>
                                    {post.thumbnail && (
                                        <div>
                                            {/* .blog-cover pins the cover to the same 920x691 box the
                                                hand-built pages use — CMS uploads come in every ratio */}
                                            <img className="blog-cover" src={post.thumbnail} alt={post.title} />
                                        </div>
                                    )}
                                    <div style={{ display: "flex", alignContent: "flex-end", width: "100%" }}>
                                        <span style={{ width: "100%", textAlign: "right", paddingTop: "16px", paddingBottom: "16px" }}>
                                            {formatBlogDate(post.publishedAt)}
                                        </span>
                                    </div>
                                    {/* Rich text authored in the CMS editor. .blog-body carries
                                        styles for the tags TipTap emits (h3, ol, blockquote, hr,
                                        links, code) so it matches the hand-built blog pages. */}
                                    <div className="blog-body" dangerouslySetInnerHTML={{ __html: post.content || '' }} />
                                </>
                            )}
                        </div>
                        <div>
                            <BlogSidebar />
                        </div>
                    </div>
                </div>
            </main>
            <div className="jjfv2">
                <FooterV2 />
            </div>
        </>
    );
}
